import { getFinalFormattedValue, TooltipLabelType, } from '@thoughtspot/formatter-service';
import { getId } from '@thoughtspot/visualization-column-util';
import chroma from 'chroma-js';
import _ from 'lodash';
import { getAnomalyColor, getCortexAnomalyList, getForecastDetailsWithExpectedColor, getForecastPoints, isShowAnomaliesEnabled, isShowAnomaliesSupported, } from '../components/kpi/cortex-utils';
import { getIncompleteThreshold, isInCompleteKPI, } from '../components/kpi/kpi-utils';
import { getMaxRadialValue, getMinRadialValue, getSeries, } from '../data/data-util';
import { getEnvFlags, getTranslatedTextCallback } from '../env';
import { ChartThemeService } from '../theme/chart-theme-service';
import { ChartType } from '../types';
import { getRadialColumn, getYAxisColumns, isCustomCalendarTimeSeries, isYAxisOrdinalBased, } from './chart-model-utils';
import { chartSpecificConstants } from './chart-type-specification-service';
function handleSizeForInnerPie(dataModel) {
    const series = getSeries(dataModel);
    series[0].size = chartSpecificConstants.PIE_SIZE;
    series[0].innerSize = chartSpecificConstants.PIE_INNERSIZE_SINGLECHART;
    if (series.length === 2) {
        series[1].size = chartSpecificConstants.PIE_OUTERSIZE_DOUBLECHART;
        series[1].dataLabels = {
            enabled: false,
        };
        series[0].innerSize = chartSpecificConstants.PIE_INNERSIZE_DOUBLECHART;
    }
}
function handleAll0Series(dataModel) {
    const series = getSeries(dataModel);
    series.forEach(serie => {
        const data = serie === null || serie === void 0 ? void 0 : serie.data;
        const yValues = [];
        data === null || data === void 0 ? void 0 : data.forEach(colData => {
            yValues.push(colData.y);
        });
        if (yValues.every(value => value <= 0)) {
            serie.visible = false;
        }
    });
}
function seriesOverrideForPie(chartModel, dataModel) {
    handleSizeForInnerPie(dataModel);
    handleAll0Series(dataModel);
}
function seriesOverrideForKPI(chartModel, dataModel) {
    var _a, _b;
    const series = getSeries(dataModel);
    const data = (_a = series[0]) === null || _a === void 0 ? void 0 : _a.data;
    const size = _.size(data);
    if (!size) {
        return;
    }
    if (!isShowAnomaliesSupported(chartModel) ||
        !isShowAnomaliesEnabled(chartModel)) {
        return;
    }
    const anomalyList = getCortexAnomalyList(chartModel, dataModel);
    if (!getEnvFlags().isKPIWithAnomalyV2Enabled && !anomalyList) {
        return;
    }
    const incompleteThreshold = getIncompleteThreshold(chartModel);
    const isCustomCalendarTimeSeriesChart = isCustomCalendarTimeSeries(chartModel);
    const enableMarker = (point) => {
        _.set(point, 'marker.enabled', true);
    };
    const setMarkerLineWidth = (point) => {
        _.set(point, 'marker.lineWidth', 0);
    };
    const enableAnomalyColor = (point, chartModel) => {
        _.set(point, 'color', getAnomalyColor(chartModel));
    };
    const enableAnomalyV2Color = (point, color) => {
        _.set(point, 'color', color);
    };
    if (getEnvFlags().isKPIWithAnomalyV2Enabled) {
        const { color, lastPoint } = getForecastDetailsWithExpectedColor(chartModel, (_b = series[0]) === null || _b === void 0 ? void 0 : _b.color, dataModel, isCustomCalendarTimeSeriesChart);
        if (lastPoint) {
            enableMarker(lastPoint);
            setMarkerLineWidth(lastPoint);
            enableAnomalyV2Color(lastPoint, color);
        }
        const yAxisColumn = _.first(getYAxisColumns(chartModel));
        const inferences = getForecastPoints(chartModel);
        const lastForecastPoint = _.last(inferences);
        const lowerBoundVal = getFinalFormattedValue(lastForecastPoint === null || lastForecastPoint === void 0 ? void 0 : lastForecastPoint.low, yAxisColumn);
        const upperBoundVal = getFinalFormattedValue(lastForecastPoint === null || lastForecastPoint === void 0 ? void 0 : lastForecastPoint.high, yAxisColumn);
        if (isShowAnomaliesEnabled(chartModel) &&
            !isInCompleteKPI(chartModel) &&
            !_.isNil(lastForecastPoint)) {
            lastPoint.additionalTooltipData = [
                {
                    label: getTranslatedTextCallback()('EXPECTED_RANGE'),
                    value: `${lowerBoundVal} to ${upperBoundVal}`,
                    labelType: TooltipLabelType.TOOLTIP_CONFIG_LABEL,
                },
            ];
        }
    }
    else {
        data.filter((point, idx) => {
            const xVal = isCustomCalendarTimeSeriesChart
                ? point.xRawValues[0]
                : point.x;
            return xVal <= incompleteThreshold && anomalyList[idx];
        }).forEach(point => {
            enableMarker(point);
            enableAnomalyColor(point, chartModel);
        });
    }
}
function seriesOverrideForBubble(chartModel, dataModel) {
    if (getRadialColumn(chartModel)) {
        let maxRadialValue = getMaxRadialValue(chartModel, dataModel);
        if (maxRadialValue > 0) {
            const MIN_BUBBLE_SIZE = 2;
            const MAX_BUBBLE_SIZE = 51;
            const minRadialValue = getMinRadialValue(chartModel, dataModel);
            if (minRadialValue < 0) {
                maxRadialValue = maxRadialValue + Math.abs(minRadialValue) + 1;
            }
            const normalizedFactor = MAX_BUBBLE_SIZE / maxRadialValue;
            _.each(getSeries(dataModel), function (s) {
                if (!s.data) {
                    return;
                }
                s.data.forEach(function (d) {
                    if (_.isNaN(Number(d.z))) {
                        return;
                    }
                    let value = d.z;
                    if (minRadialValue < 0) {
                        value = d.z + Math.abs(minRadialValue) + 1;
                    }
                    const radius = Math.max(Math.floor(normalizedFactor * value), MIN_BUBBLE_SIZE);
                    d.marker = {
                        radius,
                    };
                    d.marker.states = {
                        hover: {
                            radius: radius * 1.1,
                        },
                    };
                });
            });
        }
    }
}
function seriesOverrideForTreemap(chartModel, dataModel) {
    if (getRadialColumn(chartModel)) {
        const attrOnYAxis = isYAxisOrdinalBased(chartModel);
        _.each(getSeries(dataModel), function (s) {
            if (!s.data) {
                return;
            }
            _.each(s.data, function (d) {
                if (_.isNaN(Number(d.y)) || _.isNaN(Number(d.z))) {
                    return;
                }
                d.value = d.z;
                if (attrOnYAxis) {
                    d.color = chroma(ChartThemeService.getDefaultTheme().primaryColors[d.y])
                        .alpha(0.5)
                        .css();
                }
                else {
                    d.colorValue = d.y;
                }
                d.name = d.x;
            });
        });
    }
}
function seriesOverrideForHeatmap(chartModel, dataModel) {
    if (getRadialColumn(chartModel)) {
        _.each(getSeries(dataModel), function (s) {
            if (!s.data) {
                return;
            }
            _.each(s.data, function (d) {
                if (_.isNaN(Number(d.z))) {
                    return;
                }
                d.value = d.z;
            });
        });
    }
}
function seriesOverrideForLineColumn(chartModel, dataModel) {
    const series = getSeries(dataModel);
    const yAxisColumns = getYAxisColumns(chartModel);
    const column1 = yAxisColumns[0];
    const column1Series = series.find(function (serie) {
        return serie.blinkSeriesId === getId(column1);
    });
    if (column1Series) {
        column1Series.type = ChartType.Column.toLowerCase();
        column1Series.zIndex = 1;
    }
    const lineColumns = yAxisColumns.slice(1);
    _.each(lineColumns, function (lineColumn) {
        const lineColumnSeries = series.find(function (serie) {
            return serie.blinkSeriesId === getId(lineColumn);
        });
        if (lineColumnSeries) {
            lineColumnSeries.type = ChartType.Line.toLowerCase();
            lineColumnSeries.zIndex = 2;
        }
    });
}
function seriesOverrideForWhiskerScatter(chartModel, dataModel) {
    if (getRadialColumn(chartModel)) {
        _.each(getSeries(dataModel), s => {
            if (!s.data) {
                return;
            }
            _.each(s.data, d => {
                if (_.isNaN(Number(d.y)) || _.isNaN(Number(d.z))) {
                    return;
                }
                d.median = d.y;
                d.q1 = d.y;
                d.q3 = d.y;
                d.low = d.y - d.z;
                d.high = d.y + d.z;
                d.name = d.x;
            });
        });
    }
}
function seriesOverrideForLineStackedColumn(chartModel, dataModel) {
    const series = getSeries(dataModel);
    const totalSeries = series.length;
    const yAxisColumns = getYAxisColumns(chartModel);
    const yAxisColumnsAsLines = yAxisColumns.slice(1);
    yAxisColumnsAsLines.forEach(function (column, index) {
        const lineSeries = series[totalSeries - index - 1];
        lineSeries.type = ChartType.Line.toLowerCase();
        lineSeries.zIndex = 2;
    });
}
function seriesOverrideForCandleStick(chartModel, dataModel) {
    var _a;
    const series = getSeries(dataModel);
    (_a = series[0]) === null || _a === void 0 ? void 0 : _a.data.forEach((dataValue, idx) => {
        var _a, _b, _c;
        if (!dataValue.open) {
            dataValue.open = dataValue.y;
            dataValue.high = (_a = series[1]) === null || _a === void 0 ? void 0 : _a.data[idx].y;
            dataValue.low = (_b = series[2]) === null || _b === void 0 ? void 0 : _b.data[idx].y;
            dataValue.close = (_c = series[3]) === null || _c === void 0 ? void 0 : _c.data[idx].y;
        }
    });
    let colors = !!chartModel && chartModel.vizProp.customColorSelectorArray;
    if (!colors || colors.length === 0) {
        colors = chartSpecificConstants.DEFAULT_CANDLESTICK_COLORS;
    }
    Object.assign(series[0], {
        color: colors[1],
        lineColor: colors[1],
        upLineColor: colors[0],
        upColor: colors[0],
    });
    const blinkSeriesId = series[0].blinkSeriesId;
    series.splice(1);
}
export const seriesOverrides = {
    [ChartType.Pie]: seriesOverrideForPie,
    [ChartType.Kpi]: seriesOverrideForKPI,
    [ChartType.Bubble]: seriesOverrideForBubble,
    [ChartType.Heatmap]: seriesOverrideForHeatmap,
    [ChartType.Treemap]: seriesOverrideForTreemap,
    [ChartType.LineColumn]: seriesOverrideForLineColumn,
    [ChartType.Candlestick]: seriesOverrideForCandleStick,
    [ChartType.WhiskerScatter]: seriesOverrideForWhiskerScatter,
    [ChartType.LineStackedColumn]: seriesOverrideForLineStackedColumn,
};
//# sourceMappingURL=series-overrides.js.map